import { useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import { FaRegEye } from "react-icons/fa";
import { Modal } from "../Modal";
import MovieTile from "./MovieTile";
import { FiEdit } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { resolveHighlightUrl } from "../MovieRowForm/HighlightRowBuilder";

const minioBaseUrl = "https://images.do.cloudwalker.tv/images/";

export default function TilesList({ singleRow, setRows, rows, tilesData }) {
  const [showTile, setShowTile] = useState(false);
  const [currentTile, setCurrentTile] = useState(null);
  const [highlightPreviewUrl, setHighlightPreviewUrl] = useState(null);
  const [tooltip, setTooltip] = useState({ text: "", x: 0, y: 0, visible: false });
  const navigate = useNavigate();

  const showTooltip = (e, text) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ text, x: rect.left + rect.width / 2, y: rect.top - 4, visible: true });
  };
  const hideTooltip = () => setTooltip((prev) => ({ ...prev, visible: false }));

  const handleEditTile = (tid) => {
    navigate(`/tile/${tid}`, { state: { fromRow: true } });
  };

  const draggedIndexRef = useRef(null);

  const handleDragStart = (index) => {
    draggedIndexRef.current = index;
  };

  const handleDragOver = (e, index, rowId) => {
    e.preventDefault();

    const draggedIndex = draggedIndexRef.current;
    if (draggedIndex === index) return;

    const rowIndex = rows.findIndex((row) => row.id === rowId);
    if (rowIndex === -1) return;

    const updatedRowItems = [...rows[rowIndex].row_items];
    const draggedItem = updatedRowItems[draggedIndex];

    updatedRowItems.splice(draggedIndex, 1);
    updatedRowItems.splice(index, 0, draggedItem);

    draggedIndexRef.current = index;

    const updatedRow = { ...rows[rowIndex], row_items: updatedRowItems };
    const updatedRows = [...rows];
    updatedRows[rowIndex] = updatedRow;

    setRows(updatedRows);
  };

  const handleDragEnd = async (rowId) => {
    const rowIndex = rows.findIndex((row) => row.id === rowId);
    if (rowIndex === -1) return;

    const updatedRowItems = rows[rowIndex].row_items.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));

    const { error } = await supabase
      .from("row_data")
      .update({ row_items: updatedRowItems })
      .eq("id", rowId);

    if (error) console.error("Error updating row items:", error);
  };

  const viewTile = (item) => {
    const tile = tilesData.find((t) => t.tid === item.tid);
    setCurrentTile(tile);
    setShowTile(true);
  };

  if (!singleRow) return <div>Loading...</div>;

  return (
    <>
      {tooltip.visible && (
        <div
          className="fixed z-50 px-2 py-0.5 text-xs text-white bg-gray-800 rounded whitespace-nowrap pointer-events-none -translate-x-1/2 -translate-y-full"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.text}
        </div>
      )}

      <div className="mt-1" onClick={(e) => e.stopPropagation()}>
        {singleRow.row_items.length === 0 ? (
          <p className="text-xs text-gray-400 italic">No tiles added</p>
        ) : (
          <div className="grid grid-cols-1 gap-2">
            {singleRow.row_items.map((item, index) => {
              const isHighlight = singleRow.row_layout === "highlight";
              const tile = isHighlight ? null : tilesData.find((t) => t.tid === item.tid);
              return (
                <div
                  key={index}
                  draggable
                  onDragStart={(e) => {
                    handleDragStart(index);
                    e.currentTarget.classList.add("opacity-50", "scale-95");
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.classList.remove("opacity-50", "scale-95");
                    handleDragEnd(singleRow.id);
                  }}
                  onDragOver={(e) => handleDragOver(e, index, singleRow.id)}
                  className="p-4 py-2 cursor-grab active:cursor-grabbing flex justify-between text-xs items-center rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-all duration-300 ease-in-out hover:shadow-md"
                >
                  {isHighlight ? (
                    <div className="flex items-center gap-2 min-w-0 pr-2">
                      <img
                        src={resolveHighlightUrl(item.image, minioBaseUrl)}
                        alt=""
                        className="w-12 h-7 object-cover rounded shrink-0 bg-black/20"
                      />
                      <span className="truncate">Highlight #{index + 1}</span>
                    </div>
                  ) : (
                    <span className="block text-left break-words whitespace-normal pr-2">
                      {tile?.title || "Loading..."}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <FaRegEye
                      className="text-md cursor-pointer"
                      onMouseEnter={(e) => showTooltip(e, "View Tile")}
                      onMouseLeave={hideTooltip}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isHighlight) {
                          setHighlightPreviewUrl(resolveHighlightUrl(item.image, minioBaseUrl));
                        } else {
                          viewTile(item);
                        }
                      }}
                    />
                    {!isHighlight && (
                      <FiEdit
                        className="text-md cursor-pointer"
                        onMouseEnter={(e) => showTooltip(e, "Edit Tile")}
                        onMouseLeave={hideTooltip}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTile(item.tid);
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {highlightPreviewUrl && (
        <Modal
          customClasses="p-4 w-full max-w-3xl"
          showModal={!!highlightPreviewUrl}
          setShowModal={setHighlightPreviewUrl}
        >
          <img src={highlightPreviewUrl} alt="" className="w-full h-auto rounded" />
        </Modal>
      )}

      {currentTile && showTile && (
        <Modal
          customClasses="p-0 w-full"
          showModal={showTile}
          setShowModal={setShowTile}
        >
          <MovieTile data={[currentTile]} />
        </Modal>
      )}
    </>
  );
}
